import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Icon,
  Input,
  LoadingState,
  Modal,
  Pagination,
  useToast,
} from '@/components/ui';
import { InvoiceDownloadButton } from '@/features/studentBilling/InvoiceDownloadButton';
import { errorMessage } from '@/hooks/useApiError';

import { InvoiceTable } from './InvoiceTable';

const PAGE_SIZE = 10;

/**
 * One customer's payment history, on their admin record.
 *
 * Pass `companyId` or `studentId`. A money table stays a table: the columns are
 * compared down the page. An open invoice can be marked paid from here — for a
 * student's plan invoice that is what switches the plan on.
 */
export function CustomerInvoices({ companyId, studentId, className }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [paying, setPaying] = useState(null);
  const [reference, setReference] = useState('');

  const filter = studentId ? { student_id: studentId } : { company_id: companyId };
  const params = { page, page_size: PAGE_SIZE, ...filter };
  const invoices = useQuery({
    queryKey: ['admin-invoices', params],
    queryFn: () => adminApi.listAllInvoices(params),
    placeholderData: keepPreviousData,
  });

  const markPaid = useMutation({
    mutationFn: ({ id, note }) => adminApi.markInvoicePaid(id, note),
    onSuccess: () => {
      toast.success('Invoice marked paid');
      setPaying(null);
      setReference('');
      void queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
      });
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not mark it paid')),
  });

  const rows = invoices.data?.data ?? [];
  const meta = invoices.data?.meta;

  return (
    <Card className={className}>
      <CardHeader
        title="Invoices"
        description={
          meta && meta.total_items > 0
            ? `${meta.total_items} in total, newest first.`
            : 'Payment history, newest first.'
        }
      />
      {invoices.isPending ? (
        <LoadingState label="Loading invoices…" />
      ) : invoices.isError ? (
        <Alert tone="error" title="Could not load the invoices">
          <p>{errorMessage(invoices.error)}</p>
          <Button
            variant="secondary"
            size="xs"
            className="mt-2"
            onClick={() => void invoices.refetch()}
          >
            Try again
          </Button>
        </Alert>
      ) : (
        <>
          <InvoiceTable
            invoices={rows}
            emptyTitle="No invoices yet"
            emptyDescription={
              studentId
                ? 'Nothing has been billed to this student. Student Free is never invoiced.'
                : 'Nothing has been billed to this company. The free plan is never invoiced.'
            }
            action={(invoice) => (
              <div className="flex items-center justify-end gap-1">
                {invoice.status === 'OPEN' && (
                  <Button
                    variant="ghostPositive"
                    size="xs"
                    title={`Mark ${invoice.number} paid`}
                    onClick={() => setPaying(invoice)}
                  >
                    <Icon name="check" size={14} />
                    Mark paid
                  </Button>
                )}
                <InvoiceDownloadButton invoice={invoice} download={adminApi.downloadInvoicePdf} />
              </div>
            )}
          />
          {meta && meta.total_pages > 1 && (
            <Pagination
              className="mt-4"
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <Modal
        open={paying !== null}
        title={`Mark ${paying?.number ?? ''} paid`}
        onClose={() => setPaying(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaying(null)}>
              Cancel
            </Button>
            <Button
              disabled={reference.trim().length === 0}
              isLoading={markPaid.isPending}
              onClick={() => markPaid.mutate({ id: paying.id, note: reference.trim() })}
            >
              Mark paid
            </Button>
          </>
        }
      >
        <p className="text-ink-muted mb-4 text-sm">
          This records that money arrived and is audited against your account, so put something here
          that identifies the transfer.
        </p>
        <Field label="Payment reference">
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="UTR 4521 8890 1123"
          />
        </Field>
      </Modal>
    </Card>
  );
}
